import { Component, EventEmitter, Input, OnInit, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { unitsLib } from 'src/app/core/services/unit-library';
import { resolveUnitPrecision } from 'src/app/shared/helpers/unit-precision.helper';

export type UnitPrecisionMap = Readonly<Record<string, number>>;
export type UnitPrecisionOverrides = Readonly<Partial<Record<string, number>>>;

const DEFAULT_DECIMALS_FOR_KNOWN_UNITS = 2;

// Mirrors canonical backend enum VALUE strings from Perifor.Domain.Enums.UnitEnums.
// These are the strings validated by UserUnits.Builder.ValidateUnit(...) in backend.
// This keeps precision behavior stable even if frontend display symbols/labels differ.
const BACKEND_CANONICAL_UNIT_VALUES: readonly string[] = [
    'ft', 'm',
    'in', 'cm', 'mm',
    'd-m', 'd-ft', 'd-100m',
    '∆°C/m', '∆°F/ft', '∆°C/km',
    'kg/m', 'lb/ft', 'lb/in', 'g/m', 'g/cm',
    '°C', '°F', 'K',
    'bar', 'psi', 'KPa', 'atm',
    'tf', 'kgf', 'lbf', 'kip',
    'm/s', 'ft/s',
    'kg/m³', 'ppg (U.S.)', 'g/m³', 'g/cm³', 'g/m³', 'g/L', 'kg/l', 'sg',
    'lbm/ft³',
    'm³/min', 'GPM', 'bpm', 'LPM',
    'm³/d', 'bbl/d',
    'bbl/min',
    'm³', 'bbl', 'l',
    'cm²', 'in²', 'ft²', 'm²',
    'W/m·K', 'BTU/h·ft·°F',
    '1/°C', '1/°F',
    'Sm³/h', 'scfh', 'Sm³/hBase', 'scfhBase',
    'lb/h', 't/d', 'kg/s',
    'BTU/ft³·°F', 'J/m³·°C',
    'kPa/m', 'bar/m', 'psi/ft',
    'BTU/lbm', 'J/kg',
    'BTU', 'J',
    'lbf·ft', 'N·m',
    'Btu/ft²·°F', 'W/m²·K',
    'psi/100ft', 'bar/1000m',
    'hp(I)', 'Btu/h', 'W', 'J/h',
    'lbf/100ft²', 'Pa',
    'cP', 'Pa/s'
];

function normalizeUnitSymbol(unit: string): string {
    return unit
    .toLowerCase()
        .replace('′', '\'')
        .replace('°', 'deg')
        .replace('∆', '')
        .replace(/\^/g, '')
        .replace(/\s+/g, '');
}

const UNIT_PRECISION_OVERRIDES: UnitPrecisionMap = Object.freeze({
    'm': 5,
    'degf': 2,
    'degc': 2,
    'degc/m': 2,
    'degF/ft': 2,
    'degc/30m': 2,
    'degc/100m': 2,
    'kpa': 2,
    'mpa': 3
});

function buildDefaultUnitPrecisionMap(): UnitPrecisionMap {
    const knownUnits = new Set<string>();

    Object.keys(unitsLib).forEach((unit) => {
        knownUnits.add(normalizeUnitSymbol(unit));
    });

    BACKEND_CANONICAL_UNIT_VALUES.forEach((unit) => {
        knownUnits.add(normalizeUnitSymbol(unit));
    });

    Object.values(unitsLib).forEach((unitDef: any) => {
        if (unitDef?.symbol) {
            knownUnits.add(normalizeUnitSymbol(unitDef.symbol));
        }
    });

    const defaults = Object.fromEntries(
        Array.from(knownUnits)
            .filter((unit) => !!unit)
            .map((unit) => [unit, DEFAULT_DECIMALS_FOR_KNOWN_UNITS])
    );

    return Object.freeze({
        ...defaults,
        ...UNIT_PRECISION_OVERRIDES
    });
}

const DEFAULT_UNIT_PRECISION_MAP: UnitPrecisionMap = buildDefaultUnitPrecisionMap();

/**
 * Validation rules provided by backend
 * All constraints are optional - can have just min, just max, or both
 */
export interface ValidationRules {
    min?: number;
    max?: number;
    required?: boolean;
}

/**
 * Reusable validated number input with optional units and validation display
 * Fully encapsulated: sets up its own validators from separate value and validation inputs
 * 
 * Usage:
 * <app-validated-number-input
 *   [control]="myFormControl"
 *   [value]="backendData.temperature"
 *   [validation]="backendValidation.temperature"
 *   id="surfaceTemp"
 *   label="Surface Temperature"
 *   unit="°F"
 *   [unitPrecisionOverrides]="{ '°F': 2 }"
 *   [decimals]="1" />
 */
@Component({
    selector: 'app-validated-number-input',
    templateUrl: './validated-number-input.component.html',
    standalone: false
})
export class ValidatedNumberInputComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true }) control!: FormControl;
    @Input({ required: true }) value!: number;            // Data from backend
    @Input({ required: true }) validation!: ValidationRules;  // Rules from backend
    @Input() id: string = '';
    @Input() label: string = '';
    @Input() unit: string = '';  // Frontend controls presentation
    @Input() unitPrecisionMap: UnitPrecisionMap = DEFAULT_UNIT_PRECISION_MAP;
    @Input() unitPrecisionOverrides: UnitPrecisionOverrides = {};
    @Input() showValidation: boolean = true;
    @Input() readonly: boolean = false;  // For calculated/non-editable fields
    @Input() inline: boolean = false;
    @Input() isGrid: boolean = false;
    @Output() onChange = new EventEmitter<any>();
    private _valueChangeSubscription?: Subscription = new Subscription();

    public resolvedDecimals = 2;
    public resolvedMinFractionDigits = 1;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['unit'] || changes['unitPrecisionMap'] || changes['unitPrecisionOverrides']) {
            this.updateResolvedDecimals();
        }

        // Update control value when value input changes, but only if different
        // This prevents overwriting user edits or calculated values
        if (changes['value'] && !changes['value'].firstChange) {
            if (this.control.value !== this.value) {
                this.control.setValue(this.value, { emitEvent: false });
            }
        }

        // Handle readonly state changes through FormControl API (not [disabled] attribute)
        if (changes['readonly']) {
            if (this.readonly) {
                this.control.disable({ emitEvent: false });
            } else {
                this.control.enable({ emitEvent: false });
            }
        }

        // Set up validators when validation input changes
        if (changes['validation'] && this.validation) {
            this.setupValidators();
        }
    }

    ngOnInit(): void {
        this.updateResolvedDecimals();

        this._valueChangeSubscription = this.control.valueChanges.subscribe((value) => {
            this.onChange.emit({ value });
        });

        // Guard against validation not being set yet (can happen during component initialization)
        // Validators will be set up later in ngOnChanges when the validation input arrives
        if (!this.validation) {
            return;
        }

        // Component sets up its own validators from separate inputs
        // Only set initial value if control is empty
        if (this.control.value == null) {
            this.control.setValue(this.value, { emitEvent: false });
        }

        this.setupValidators();
    }

    ngOnDestroy(): void {
        this._valueChangeSubscription?.unsubscribe();
    }

    private setupValidators(): void {
        if (!this.validation) return;

        const validators = [];
        if (this.validation.required !== false) {
            validators.push(Validators.required);
        }
        if (this.validation.min !== undefined) {
            validators.push(Validators.min(this.validation.min));
        }
        if (this.validation.max !== undefined) {
            validators.push(Validators.max(this.validation.max));
        }

        this.control.setValidators(validators);
        this.control.updateValueAndValidity({ emitEvent: false });

        // Set initial readonly state through FormControl API
        if (this.readonly) {
            this.control.disable({ emitEvent: false });
        }
    }

    private updateResolvedDecimals(): void {
        this.resolvedDecimals = resolveUnitPrecision(this.unit, this.unitPrecisionMap, this.unitPrecisionOverrides);
        this.resolvedMinFractionDigits = Math.min(1, this.resolvedDecimals);
    }

    /**
     * Generate user-friendly validation message from Angular validator errors
     * Extracts min/max values directly from error metadata
     */
    getValidationMessage(): string {
        if (!this.control.errors) return '';

        if (this.control.errors['min']) {
            const unit = this.unit ? ` ${this.unit}` : '';
            return `Must be at least ${this.control.errors['min'].min}${unit}`;
        }
        if (this.control.errors['max']) {
            const unit = this.unit ? ` ${this.unit}` : '';
            return `Must not exceed ${this.control.errors['max'].max}${unit}`;
        }
        if (this.control.errors['required']) {
            return 'Required';
        }
        return 'Invalid value';
    }

}
